import { Box } from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { getLpSuppliers } from '../../../../features/master-data';
import { StorageKey } from '../../../../shared/auth/DataService';
import AddRowToolbar from '../../../../shared/components/datagrid/AddRowToolbar';
import getActionsColumn from '../../../../shared/components/datagrid/getActionsColumn';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import useRowEditing from '../../../../shared/hooks/useRowEditing';
import useSnackbar from '../../../../shared/hooks/useSnackbar';
import { scrollToNewRow } from '../../../../shared/utils/scrollToNewRow';
import {
  addLpSupplier,
  deleteLpSupplier,
  updateLpSupplier,
} from '../../services/SettingsRepository';

const SupplierTable = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [nextId, setNextId] = useState(1);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const apiRef = useGridApiRef();

  const {
    rowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    startEditingRow,
  } = useRowEditing({ rows: suppliers, setRows: setSuppliers });

  const fetchSuppliers = async () => {
    try {
      const suppliersResponse = await getLpSuppliers();
      setSuppliers(
        suppliersResponse.map((supplier, index) => ({
          ...supplier,
          id: index + 1,
        }))
      );
      setNextId(
        suppliersResponse.length
          ? Math.max(suppliersResponse.map((s) => s.id)) + 1
          : 1
      );
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      showSnackbar('Error fetching suppliers.', 'error');
    }
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteClick = (id) => async () => {
    const rowToDelete = suppliers.find((row) => row.id === id);
    try {
      await deleteLpSupplier(rowToDelete.supplierNo);
      await fetchSuppliers();
      showSnackbar('Supplier deleted successfully!');
    } catch (error) {
      console.error('Error deleting supplier:', error);
      showSnackbar('Error deleting supplier.', 'error');
    }
  };

  const processRowUpdate = async (newRow) => {
    try {
      if (newRow.isNew) {
        const emea = localStorage.getItem(StorageKey.userInfo_sub);
        await addLpSupplier({ ...newRow, createdBy: { emea } });
        showSnackbar('Supplier added successfully!');
      } else {
        await updateLpSupplier(newRow.supplierNo, newRow);
        showSnackbar('Supplier updated successfully!');
      }
      await fetchSuppliers();
    } catch (error) {
      console.error('Error saving supplier:', error);
      showSnackbar('Error saving supplier.', 'error');
      if (newRow.isNew) {
        setSuppliers((prev) => prev.filter((row) => row.id !== newRow.id));
      }
      throw error;
    }
    return { ...newRow, isNew: false };
  };

  const handleAddClick = () => {
    const newId = nextId;
    setNextId(newId + 1);
    setSuppliers((oldRows) => [
      ...oldRows,
      {
        id: newId,
        supplierNo: '',
        supplierName: '',
        isPortOperator: false,
        isShippingCompany: false,
        isNew: true,
      },
    ]);
    startEditingRow(newId, 'supplierNo');
    // Clear any active filters so the new row is visible, then scroll to it
    apiRef.current?.setFilterModel({ items: [] });
    scrollToNewRow(apiRef, suppliers.length + 1);
  };

  const supplierColumns = [
    { field: 'id', headerName: 'ID', width: 150, hide: true },
    {
      field: 'supplierNo',
      headerName: 'Supplier No',
      width: 150,
      editable: true,
    }, // controlled by isCellEditable
    {
      field: 'supplierName',
      headerName: 'Supplier Name',
      width: 300,
      editable: true,
    },
    {
      field: 'isPortOperator',
      headerName: 'Is Port Operator',
      type: 'boolean',
      width: 200,
      editable: true,
    },
    {
      field: 'isShippingCompany',
      headerName: 'Is Shipping Company',
      type: 'boolean',
      width: 200,
      editable: true,
    },
    getActionsColumn({
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
    }),
  ];

  return (
    <Box height='50vh'>
      <DataGridPro
        apiRef={apiRef}
        pagination
        rows={suppliers}
        columns={supplierColumns}
        getRowId={(row) => row.id}
        editMode='row'
        // supplierNo is editable only for new rows; isCellEditable prevents editing on existing rows
        isCellEditable={(params) =>
          params.field !== 'supplierNo' || !!params.row.isNew
        }
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          console.error('Row update error:', error)
        }
        slots={{ toolbar: AddRowToolbar }}
        slotProps={{
          toolbar: { label: 'Add Supplier', onAdd: handleAddClick },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          columns: { columnVisibilityModel: { id: false } },
        }}
      />
      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

export default SupplierTable;
